import React, { createContext, useContext, useState } from 'react';
import { Form } from 'antd';
import { useApplicationContext } from '../v1/Application/context';
import {
	State,
	Methods,
	Context,
	ReviewResponse,
	InitialValuesProps,
	AssessmentResponse,
	Resolution886ReportResponse,
	Resolution886ReportProviderProps,
	StepsNames,
	StatusAssessment,
	ActionPlanResponse,
	PreviousReview,
	RelatedReport
} from './types';

const Resolution886ReportContext = createContext<Context>({} as Context);

const { useForm } = Form;

export const Resolution886ReportProvider = ({
	children,
	resolution886,
	customReport
}: Readonly<Resolution886ReportProviderProps>) => {
	const { organization, company } = useApplicationContext();

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [categoryId, setCategoryId] = useState<string>('');
	const [openActionPlan, setOpenActionPlan] = useState<boolean>(false);
	const [assessment, setAssessment] = useState<AssessmentResponse>({} as AssessmentResponse);

	const [reportId, setReportId] = useState<string>();
	const [report, setReport] = useState<Resolution886ReportResponse>({} as Resolution886ReportResponse);
	const [review, setReview] = useState<ReviewResponse>({} as ReviewResponse);

	const [lastReviewId, setLastReviewId] = useState<string>();
	const [lastReview, setLastReview] = useState<ReviewResponse>({} as ReviewResponse);
	const [previousReview, setPreviousReview] = useState<PreviousReview>({} as PreviousReview);
	const [newReview, setNewReview] = useState<boolean>(false);
	const [newReviewCreated, setNewReviewCreated] = useState<boolean>(false);
	const [firstReview, setFirstReview] = useState<boolean>(true);
	const [stepsNames, setStepsNames] = useState<StepsNames>({} as StepsNames);
	const [categoryResultId, setCategoryResultId] = useState<string>();
	const [isTolerable, setIsTolerable] = useState<boolean>(true);
	const [actionPlan, setActionPlan] = useState<ActionPlanResponse>({} as ActionPlanResponse);
	const [titleActionPlan, setTitleActionPlan] = useState<string>();
	const [selectedReviewId, setSelectedReviewId] = useState<string>('initial');
	const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>();
	const [relatedReport, setRelatedReport] = useState<RelatedReport | null>(null);
	const [reviewConsolidated, setReviewConsolidated] = useState<boolean>(false);

	const [form] = useForm();

	const initialValues: InitialValuesProps = {
		id: null,
		file_list_id: [],
		general_information: {
			company_id: company.id,
			organization_id: organization.id,
			review_id: null,
			name: null,
			workstation_id: null,
			sector_id: null,
			line_id: null,
			employer_id: null,
			evaluator_id: null,
			occupational_doctor_id: null,
			safety_and_hygiene_responsible_id: null,
			symptom_location: null,
			workers_number: null,
			early_manifestation: false,
			written_work_procedure: true,
			training: true,
			worker_list_id: []
		},
		evaluation: {
			activities: []
		},
		action_plan: {},
		result: {}
	};

	function setInitialValues(values: InitialValuesProps): void {
		form.setFieldsValue(values);
	}

	function handlePreviousStep(): void {
		setCurrentStep((prevState) => prevState - 1);
	}

	function handleNextStep(): void {
		setCurrentStep((prevState) => prevState + 1);
	}

	function handleCurrentStep(step: number): void {
		setCurrentStep(step);
	}

	async function handleStepNavigationClick(step: number): Promise<void> {
		setCurrentStep(step);
	}

	function handleActionPlan(title?: string): void {
		if (title) {
			handleSetTitleActionPlan(title);

			if (actionPlan?.title) {
				handleSetTitleActionPlan(actionPlan?.title);
			}
		}

		setOpenActionPlan((prevState) => !prevState);
	}

	function handleSetReport(data: Resolution886ReportResponse): void {
		setFirstReview(data.reviews.length === 1);
		setReport(data);
	}

	function handleSetLastReviewId(id: string): void {
		setLastReviewId(id);
	}

	function handleSetReportId(id: string): void {
		setReportId(id);
	}

	function handleSetNewReview(): void {
		setNewReview(true);
	}

	function handleSetLastReview(review: ReviewResponse): void {
		setLastReview(review);
	}

	function handleSetStepsNames(stepsNames: StepsNames): void {
		setStepsNames(stepsNames);
	}

	function handleSetCategoryResultId(id: string): void {
		setCategoryResultId(id);
	}

	function handleSetIsTolerable(isTolerable: boolean): void {
		setIsTolerable(isTolerable);
	}

	function handleSetReview(review: ReviewResponse): void {
		setReview(review);
	}

	function handleSetPreviousReview(previousReview: PreviousReview): void {
		setPreviousReview(previousReview);
	}

	function handleSetActionPlan(actionPlan: ActionPlanResponse): void {
		setActionPlan(actionPlan);
	}

	function handleSetTitleActionPlan(title: string): void {
		setTitleActionPlan(title);
	}

	function handleSetSelectedReviewId(reviewId: string): void {
		setSelectedReviewId(reviewId);
	}

	function handleSetSelectedAssessmentId(assessmentId: string): void {
		setSelectedAssessmentId(assessmentId);
	}

	function handleSetRelatedReport(relatedReport: RelatedReport | null): void {
		setRelatedReport(relatedReport);
	}

	function handleSetReviewConsolidated(consolidated: boolean): void {
		setReviewConsolidated(consolidated);
	}

	function mapDataToInitialValues(
		data: Resolution886ReportResponse,
		review: ReviewResponse,
		isNewReview: boolean
	): InitialValuesProps {
		const activities = review.assessments.map((assessment: AssessmentResponse) => ({
			file_id: assessment.file.id,
			file_name: assessment.file.original_name,
			activity_id: assessment.activity?.id ?? null,
			activity_name: assessment.activity?.name ?? null,
			assessment_id: assessment.id,
			status: assessment.status || StatusAssessment.NOT_AVALIATED
		}));

		return {
			id: data.id,
			file_list_id: isNewReview ? [] : review.assessments.map((a: AssessmentResponse) => a.file.id),
			general_information: {
				review_id: !newReviewCreated && isNewReview ? null : review.id,
				name: review.name,
				workstation_id: data.workstation.id,
				sector_id: data.sector.id,
				line_id: data.line.id,
				company_id: data.company.id,
				organization_id: data.organization.id,
				employer_id: review.employer.id,
				evaluator_id: review.evaluator.id,
				occupational_doctor_id: review.occupational_doctor.id,
				safety_and_hygiene_responsible_id: review.safety_and_hygiene_responsible.id,
				symptom_location: review.symptom_location,
				workers_number: review.workers_number,
				early_manifestation: review.early_manifestation === 0 ? false : true,
				written_work_procedure: review.written_work_procedure === 0 ? false : true,
				training: review.training === 0 ? false : true,
				worker_list_id: review.workers.map((w: { id: string; name: string }) => w.id)
			},
			evaluation: { activities },
			action_plan: {},
			result: {}
		};
	}

	const states: State = {
		form,
		categoryId,
		currentStep,
		customReport,
		openActionPlan,
		resolution886,
		initialValues,
		lastReviewId,
		assessment,
		report,
		newReview,
		lastReview,
		reportId,
		firstReview,
		stepsNames,
		categoryResultId,
		isTolerable,
		review,
		actionPlan,
		titleActionPlan,
		newReviewCreated,
		previousReview,
		selectedReviewId,
		selectedAssessmentId,
		relatedReport,
		reviewConsolidated
	};

	const methods: Methods = {
		setCategoryId,
		handleCurrentStep,
		handleNextStep,
		handleActionPlan,
		handlePreviousStep,
		handleStepNavigationClick,
		setInitialValues,
		setAssessment,
		handleSetReport,
		handleSetLastReviewId,
		mapDataToInitialValues,
		handleSetNewReview,
		handleSetLastReview,
		handleSetReportId,
		handleSetStepsNames,
		handleSetCategoryResultId,
		handleSetIsTolerable,
		handleSetReview,
		handleSetActionPlan,
		setNewReviewCreated,
		handleSetPreviousReview,
		handleSetSelectedReviewId,
		handleSetSelectedAssessmentId,
		handleSetRelatedReport,
		handleSetReviewConsolidated
	};

	const context: Context = {
		...states,
		...methods
	};

	return <Resolution886ReportContext.Provider value={context}>{children}</Resolution886ReportContext.Provider>;
};

export const useResolution886Report = (): Context => {
	const context = useContext(Resolution886ReportContext);

	if (!context) {
		throw new Error('Resolution886 context not found!');
	}

	return context;
};
